import React from 'react';
import { format, parseISO } from 'date-fns';
import { Building2, User, MoreHorizontal, CheckCircle2, XCircle, ArrowUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { PipelineState, Deal } from './types';
import { cn } from '@/lib/utils';

interface DealTableViewProps {
  state: PipelineState;
  onDealClick: (deal: Deal) => void;
}

const PRIORITY_BADGE: Record<string, { label: string; className: string }> = {
  critical: { label: 'Critical', className: 'bg-red-500/10 text-red-500 border-red-500/20' },
  high: { label: 'High', className: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
  medium: { label: 'Medium', className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  low: { label: 'Low', className: 'bg-muted text-muted-foreground border-border' },
};

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  won: { label: 'Won', className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
  lost: { label: 'Lost', className: 'bg-rose-500/10 text-rose-500 border-rose-500/20' },
};

export const DealTableView: React.FC<DealTableViewProps> = ({ state, onDealClick }) => {
  const deals = Object.values(state.dealsById || {});

  if (deals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] bg-card rounded-2xl border border-border p-8 text-center">
        <p className="text-base font-semibold text-foreground">No deals found</p>
        <p className="text-sm text-muted-foreground mt-1">Create a new deal to get started.</p>
      </div>
    );
  }

  return (
    <div className="h-full bg-card rounded-2xl border border-border overflow-hidden shadow-sm flex flex-col">
      <div className="flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr className="border-b border-border bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wider sticky top-0 z-10 backdrop-blur-sm">
              <th className="py-3 px-4">Deal Name</th>
              <th className="py-3 px-4">Stage</th>
              <th className="py-3 px-4">Value</th>
              <th className="py-3 px-4">Priority</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Client / Company</th>
              <th className="py-3 px-4">Expected Close</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {deals.map((deal) => {
              const stage = deal.stage_id ? state.stageMeta[deal.stage_id] : null;
              const priorityInfo = PRIORITY_BADGE[deal.priority] || PRIORITY_BADGE.medium;
              const statusInfo = STATUS_BADGE[deal.status] || STATUS_BADGE.active;

              let formattedDate = '—';
              if (deal.expectedCloseDate) {
                try {
                  formattedDate = format(parseISO(deal.expectedCloseDate), 'MMM d, yyyy');
                } catch {
                  formattedDate = deal.expectedCloseDate;
                }
              }

              return (
                <tr
                  key={deal.id}
                  onClick={() => onDealClick(deal)}
                  className="hover:bg-muted/40 transition-colors cursor-pointer group"
                >
                  <td className="py-3 px-4">
                    <div className="font-semibold text-foreground group-hover:text-workspace-accent transition-colors flex items-center gap-1.5">
                      {deal.title}
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
                    </div>
                    {deal.description && (
                      <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {deal.description}
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    {stage ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-muted text-foreground border border-border">
                        <span className="w-1.5 h-1.5 rounded-full bg-workspace-accent" />
                        {stage.title}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap font-bold text-foreground">
                    ${(deal.value || 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border', priorityInfo.className)}>
                      {priorityInfo.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border', statusInfo.className)}>
                      {statusInfo.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <div className="flex flex-col gap-0.5">
                      {deal.company?.name && (
                        <span className="flex items-center gap-1 text-xs text-foreground font-medium">
                          <Building2 className="w-3 h-3 text-muted-foreground" />
                          {deal.company.name}
                        </span>
                      )}
                      {deal.contact?.name && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <User className="w-3 h-3 text-muted-foreground" />
                          {deal.contact.name}
                        </span>
                      )}
                      {!deal.company?.name && !deal.contact?.name && (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap text-xs text-muted-foreground">
                    {formattedDate}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
